"use client";

import { Pencil, FileInput, RotateCw, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import logger from "@/lib/logger";
import { fetchWithAuth } from "@/lib/http";
import useClientTranslation from "@/hooks/useClientTranslation";

const USD_TO_CNY = 7;

function formatCost(rawCost: string, locale: string): string {
  if (!rawCost || isNaN(Number(rawCost))) return rawCost;
  const usd = Number(rawCost) / 500000;
  if (locale === "zh") {
    return `¥${(usd * USD_TO_CNY).toFixed(4)}`;
  }
  return `$${usd.toFixed(4)}`;
}

interface LogEntry {
  id: string;
  timestamp: string; // human readable
  type: string;
  tokenName: string;
  tokenGroup: string;
  model: string;
  duration: string;
  stream: string; // "是" | "否"
  promptTokens: number;
  completionTokens: number;
  costAmount: string;
  ip: string;
  description: string;
}

interface Props {
  filters?: {
    startDate?: string;
    endDate?: string;
    tokenName?: string;
    tokenGroup?: string;
    logType?: string;
    model?: string;
    minUseTime?: string;
    stream?: string;
    ip?: string;
    recentCount?: number;
  };
  onRefresh?: () => void;
  refreshCount?: number;
  logs?: LogEntry[];
  onLogsChange?: (logs: LogEntry[]) => void;
  selectedGroup?: string | null;
}

export default function LogTable({
  filters = {},
  onRefresh,
  refreshCount = 0,
  logs: externalLogs = [],
  onLogsChange,
  selectedGroup,
}: Props) {
  const { t, locale } = useClientTranslation("logpg");
  const [selectedLogs, setSelectedLogs] = useState<Set<number>>(new Set());
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshTurns, setRefreshTurns] = useState(0);
  const refreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const PAGE_SIZE_OPTIONS = [50, 100, 200, 500, 1000, 5000, 10000];
  const [batchExportOpen, setBatchExportOpen] = useState(false);
  const [exportEmail, setExportEmail] = useState("");
  const [exportCount, setExportCount] = useState("10000");
  const [batchExporting, setBatchExporting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // 自动刷新：页面活跃时每5秒刷新一次
  useEffect(() => {
    if (!autoRefresh) {
      if (autoRefreshTimer.current) {
        clearInterval(autoRefreshTimer.current);
        autoRefreshTimer.current = null;
      }
      return;
    }

    const tick = () => {
      if (document.visibilityState === "visible") {
        onRefresh?.();
      }
    };

    autoRefreshTimer.current = setInterval(tick, 5000);

    return () => {
      if (autoRefreshTimer.current) {
        clearInterval(autoRefreshTimer.current);
        autoRefreshTimer.current = null;
      }
    };
  }, [autoRefresh, onRefresh]);

  // 将后端原始日志对象规范化为表格需要的字段
  function mapType(type: any) {
    if (typeof type === "number") {
      switch (type) {
        case 0: return "unknown";
        case 1: return "recharge";
        case 2: return "consumption";
        case 3: return "admin";
        case 4: return "system";
        case 5: return "error";
        case 6: return "refund";
        case 7: return "calling";
        default: return `type:${type}`;
      }
    }
    return String(type ?? "");
  }

  // 渲染时翻译日志类型
  function renderType(typeKey: string) {
    if (typeKey.startsWith("type:")) {
      return `${t("logTypes.type")}:${typeKey.slice(5)}`;
    }
    const knownKeys = ["unknown", "recharge", "consumption", "admin", "system", "error", "refund", "calling"];
    if (knownKeys.includes(typeKey)) {
      return t(`logTypes.${typeKey}`);
    }
    return typeKey;
  }

  function parseCostFromContent(content?: string) {
    if (!content) return "";
    // 支持 $, ＄, ￥ 等符号，或 "充值金额：数字"
    const m = content.match(/[\$＄￥]\s*([0-9]+(?:\.[0-9]+)?)/);
    if (m && m[1]) return m[1];
    const m2 = content.match(/充值金额[:：]\s*([0-9]+(?:\.[0-9]+)?)/);
    if (m2 && m2[1]) return m2[1];
    return "";
  }

  function normalizeLog(raw: any): LogEntry {
    const id = raw.id != null ? String(raw.id) : "";
    const timestamp = raw.timestamp
      ? String(raw.timestamp)
      : raw.created_at
      ? new Date(Number(raw.created_at) * 1000).toLocaleString()
      : "";

    const type = mapType(raw.type);

    const tokenName = raw.tokenName ?? raw.token_name ?? raw.username ?? "";
    const tokenGroup = raw.tokenGroup ?? raw.group ?? "";
    const model = raw.model ?? raw.model_name ?? "";
    const duration = raw.duration ?? (raw.use_time ? `${raw.use_time}s` : "");
    const stream =
      raw.stream ?? (typeof raw.is_stream === "boolean" ? (raw.is_stream ? "yes" : "no") : "no");

    const promptTokens = Number(raw.promptTokens ?? raw.prompt_tokens ?? 0) || 0;
    const completionTokens = Number(raw.completionTokens ?? raw.completion_tokens ?? 0) || 0;

    const costAmount = String(
      raw.costAmount ?? raw.cost_amount ?? (raw.quota != null && raw.quota !== 0 ? raw.quota : parseCostFromContent(raw.content ?? raw.description)) ?? ""
    );

    const ip = raw.ip ?? "";
    const description = raw.description ?? raw.content ?? "";

    return {
      id,
      timestamp,
      type,
      tokenName,
      tokenGroup,
      model,
      duration,
      stream,
      promptTokens,
      completionTokens,
      costAmount,
      ip,
      description,
    };
  }
  // 同步外部日志列表
  useEffect(() => {
    if (externalLogs && externalLogs.length > 0) {
      const normalized = externalLogs.map(normalizeLog);
      setLogs(normalized);
      onLogsChange?.(normalized);
    }
  }, [externalLogs]);

  // 筛选条件或分组变化时重置页码
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedGroup,
    filters.startDate,
    filters.endDate,
    filters.tokenName,
    filters.tokenGroup,
    filters.logType,
    filters.model,
    filters.minUseTime,
    filters.stream,
    filters.ip,
    filters.recentCount,
  ]);

  // 同步 recentCount 到 pageSize
  useEffect(() => {
    if (filters.recentCount && filters.recentCount > 0) {
      setPageSize(filters.recentCount);
    }
  }, [filters.recentCount]);

  // 从 API 获取日志数据
  useEffect(() => {
    const fetchLogs = async () => {
      // 如果有外部日志则不需要获取
      if (externalLogs && externalLogs.length > 0) return;

      try {
        setLoading(true);

        // 构建查询参数
        const params = new URLSearchParams();
        params.append("page", String(currentPage));
        params.append("page_size", String(pageSize));

        if (selectedGroup) params.append("user_id", selectedGroup);
        if (filters.tokenName) params.append("token_name", filters.tokenName);
        if (filters.tokenGroup) params.append("token_group", filters.tokenGroup);
        if (filters.logType) params.append("type", filters.logType);
        if (filters.model) params.append("model_name", filters.model);
        if (filters.startDate) params.append("start_timestamp", String(Math.floor(new Date(filters.startDate).getTime() / 1000)));
        if (filters.endDate) params.append("end_timestamp", String(Math.floor(new Date(filters.endDate).getTime() / 1000)));
        if (filters.minUseTime) params.append("min_use_time", filters.minUseTime);
        if (filters.stream) params.append("stream", filters.stream);
        if (filters.ip) params.append("ip", filters.ip);

        const response = await fetchWithAuth(`/api/log?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if(!("success" in data) || data.success === false) {
          throw new Error(data.message || t("messages.fetchFailed"));
        }
        // 假设后端返回直接是日志数组或包含logs字段的对象
        if (!data.data || !Array.isArray(data.data.items)) {
          throw new Error(t("messages.invalidFormat"));
        }
        const rawLogs = data.data.items;
        const total = data.data.total ?? rawLogs.length;
        setTotalCount(total);

        // 超过 10000 条时提示用户
        if (total > 10000) {
          toast.warning(t("messages.tooManyLogs") ?? "最多查询 10000 条日志，超出部分请使用批量导出功能");
        }

        // 规范化后端日志结构以适配表格展示
        const logsData = rawLogs.map(normalizeLog);

        setLogs(logsData);
        onLogsChange?.(logsData);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        // 后端不可用是预期情况，仅记录日志以便排查
        logger.debug("获取日志失败: %s", msg);
        setLogs([]);
        onLogsChange?.([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refreshCount,
    selectedGroup,
    currentPage,
    pageSize,
    filters.startDate,
    filters.endDate,
    filters.tokenName,
    filters.tokenGroup,
    filters.logType,
    filters.model,
    filters.minUseTime,
    filters.stream,
    filters.ip,
  ]); // 只监听 filters 的各个字段值和选中的分组

  // 简单过滤：根据过滤条件进行字符串包含匹配
  const filtered = logs.filter(row => {
    if (filters.tokenName && !row.tokenName.includes(filters.tokenName)) return false;
    if (filters.tokenGroup && !row.tokenGroup.includes(filters.tokenGroup)) return false;
    if (filters.model && !row.model.includes(filters.model)) return false;
    if (filters.stream && row.stream !== (filters.stream === "1" ? "yes" : "no")) return false;
    if (filters.ip && !row.ip.includes(filters.ip)) return false;

    return true;
  });

  /**
   * 切换日志描述展开/收起
   */
  const toggleExpand = (index: number) => {
    const next = new Set(expandedRows);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setExpandedRows(next);
  };

  /**
   * 切换多选模式
   */
  const handleToggleMultiSelect = () => {
    setMultiSelectMode(!multiSelectMode);
    setSelectedLogs(new Set()); // 切换时清空已选
  };

  /**
   * 选择/取消选择单条日志
   */
  const handleSelectLog = (index: number) => {
    const newSelected = new Set(selectedLogs);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedLogs(newSelected);
  };

  /**
   * 全选/取消全选
   */
  const handleSelectAll = () => {
    if (selectedLogs.size === filtered.length) {
      setSelectedLogs(new Set());
    } else {
      setSelectedLogs(new Set(filtered.map((_, i) => i)));
    }
  };

  /**
   * 导出日志信息为 CSV（支持中文）
   * 无选中时，重新请求 API 获取当前查询条件下的全部日志再导出
   */
  const handleExport = async () => {
    let dataToExport: LogEntry[];

    if (selectedLogs.size > 0) {
      dataToExport = Array.from(selectedLogs).map(i => filtered[i]);
    } else {
      // 请求全部匹配日志
      try {
        toast.info(t("messages.exporting"));
        const params = new URLSearchParams();
        params.append("page", "1");
        params.append("page_size", String(Math.min(totalCount || 10000, 10000)));
        if (selectedGroup) params.append("user_id", selectedGroup);
        if (filters.tokenName) params.append("token_name", filters.tokenName);
        if (filters.tokenGroup) params.append("token_group", filters.tokenGroup);
        if (filters.logType) params.append("type", filters.logType);
        if (filters.model) params.append("model_name", filters.model);
        if (filters.startDate) params.append("start_timestamp", String(Math.floor(new Date(filters.startDate).getTime() / 1000)));
        if (filters.endDate) params.append("end_timestamp", String(Math.floor(new Date(filters.endDate).getTime() / 1000)));
        if (filters.minUseTime) params.append("min_use_time", filters.minUseTime);
        if (filters.stream) params.append("stream", filters.stream);
        if (filters.ip) params.append("ip", filters.ip);

        const response = await fetchWithAuth(`/api/log?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (!("success" in data) || data.success === false) {
          throw new Error(data.message || t("messages.fetchFailed"));
        }
        if (!data.data || !Array.isArray(data.data.items)) {
          throw new Error(t("messages.invalidFormat"));
        }
        dataToExport = data.data.items.map(normalizeLog);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(msg);
        return;
      }
    }

    const headers = [
      t("exportHeaders.time"),
      t("exportHeaders.type"),
      t("exportHeaders.tokenName"),
      t("exportHeaders.tokenGroup"),
      t("exportHeaders.model"),
      t("exportHeaders.duration"),
      t("exportHeaders.stream"),
      t("exportHeaders.prompt"),
      t("exportHeaders.completion"),
      t("exportHeaders.cost"),
      t("exportHeaders.ip"),
      t("exportHeaders.description"),
    ];

    const rows = dataToExport.map(row => [
      row.timestamp,
      renderType(row.type),
      row.tokenName,
      row.tokenGroup,
      row.model,
      row.duration,
      row.stream === "yes" ? t("yes") : t("no"),
      row.promptTokens,
      row.completionTokens,
      row.costAmount && !isNaN(Number(row.costAmount))
        ? formatCost(row.costAmount, locale)
        : row.costAmount,
      row.ip,
      row.description,
    ]);

    // 构建 CSV 内容，使用 BOM 字符确保中文正确显示
    const BOM = "\uFEFF";
    const csvContent = [
      headers.map(h => `"${h}"`).join(","),
      ...rows.map((row: (string | number)[]) => row.map((cell: string | number) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `logs-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(t("messages.exportSuccess"));
  };

  /**
   * 刷新日志列表
   */
  const handleRefresh = () => {
    setRefreshTurns(t => t + 1);
    // 连续点击只触发最后一次（尾随防抖）
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }

    refreshTimeout.current = setTimeout(() => {
      // console.log("刷新日志列表");
      onRefresh && onRefresh();
    }, 300);
  };

  /**
   * 打开批量导出确认弹窗，读取账号绑定邮箱作为默认值
   */
  const handleBatchExport = () => {
    setExportEmail("");
    setExportCount("10000");
    setBatchExportOpen(true);
  };

  /**
   * 确认批量导出，发送到指定邮箱
   */
  const handleBatchExportConfirm = async () => {
    if (!exportEmail.trim()) {
      toast.error(t("batchExportDialog.emailRequired"));
      return;
    }
    // 限制导出时间范围：最近2个月
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const twoMonthsAgoStr = twoMonthsAgo.toISOString().slice(0, 10);

    if (filters.startDate && new Date(filters.startDate) < twoMonthsAgo) {
      toast.error(t("batchExportDialog.twoMonthLimit"));
      return;
    }
    try {
      setBatchExporting(true);
      const params = new URLSearchParams();
      params.append("page", "1");
      params.append("page_size", String(Math.min(Number(exportCount) || 10000, 100000)));
      params.append("email", exportEmail.trim());
      if (selectedGroup) params.append("user_id", selectedGroup);
      if (filters.tokenName) params.append("token_name", filters.tokenName);
      if (filters.tokenGroup) params.append("token_group", filters.tokenGroup);
      if (filters.logType) params.append("type", filters.logType);
      if (filters.model) params.append("model_name", filters.model);
      if (filters.startDate) params.append("start_timestamp", String(Math.floor(new Date(filters.startDate).getTime() / 1000)));
      if (filters.endDate) params.append("end_timestamp", String(Math.floor(new Date(filters.endDate).getTime() / 1000)));
      if (filters.minUseTime) params.append("min_use_time", filters.minUseTime);
      if (filters.stream) params.append("stream", filters.stream);
      if (filters.ip) params.append("ip", filters.ip);

      toast.info(t("messages.exporting"));
      const response = await fetchWithAuth(`/api/log/export?${params.toString()}`, {
        method: "GET",
      });
      const data = await response.json();
      if (!("success" in data) || data.success === false) {
        throw new Error(data.message || t("messages.fetchFailed"));
      }
      toast.success(t("batchExportDialog.sendSuccess"));
      setBatchExportOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(msg);
    } finally {
      setBatchExporting(false);
    }
  };

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, []);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg shrink-0 mr-4">{t("tableTitle")}</CardTitle>
        <div className="flex gap-1.5 items-center">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleToggleMultiSelect}>
            <Pencil size={12} />
            {multiSelectMode ? t("cancelMultiSelect") : t("multiSelect")}
          </Button>
          <Button size="sm" className="h-7 text-xs" onClick={handleExport}>
            <FileInput size={12} />
            {t("export")}
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleBatchExport}>
            <Download size={12} />
            {t("batchExport") ?? "批量导出"}
          </Button>
          <div className="flex items-center gap-1.5 ml-auto">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              className="scale-75"
            />
            <Label htmlFor="auto-refresh" className="text-xs cursor-pointer whitespace-nowrap">
              {t("autoRefresh")}
            </Label>
          </div>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleRefresh}>
            <RotateCw
              size={12}
              style={{
                transform: `rotate(${refreshTurns * 360}deg)`,
                transition: "transform 0.6s ease",
              }}
            />
            {t("refresh")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="overflow-x-auto">
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              {multiSelectMode && (
                <TableHead className="w-8 px-2">
                  <Checkbox
                    checked={selectedLogs.size === filtered.length && filtered.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
              )}
              <TableHead className="whitespace-nowrap">{t("tableHeaders.time")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.type")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.tokenName")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.tokenGroup")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.model")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.duration")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.stream")}</TableHead>
              <TableHead className="whitespace-nowrap text-right">{t("tableHeaders.prompt")}</TableHead>
              <TableHead className="whitespace-nowrap text-right">{t("tableHeaders.completion")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.cost")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.ip")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("tableHeaders.description")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length > 0 ? (
              filtered.map((row, i) => (
                <TableRow
                  key={i}
                  data-state={selectedLogs.has(i) ? "selected" : undefined}
                  className="h-8"
                >
                  {multiSelectMode && (
                    <TableCell className="w-8 px-2">
                      <Checkbox
                        checked={selectedLogs.has(i)}
                        onCheckedChange={() => handleSelectLog(i)}
                      />
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap">{row.timestamp}</TableCell>
                  <TableCell className="whitespace-nowrap">{renderType(row.type)}</TableCell>
                  <TableCell>{row.tokenName}</TableCell>
                  <TableCell>{row.tokenGroup}</TableCell>
                  <TableCell>{row.model}</TableCell>
                  <TableCell>{row.duration}</TableCell>
                  <TableCell>
                    <Badge variant={row.stream === "yes" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
                      {row.stream === "yes" ? t("yes") : t("no")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.promptTokens}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.completionTokens}</TableCell>
                  <TableCell className="text-purple-600 dark:text-purple-400 tabular-nums">
                    {formatCost(row.costAmount, locale)}
                  </TableCell>
                  <TableCell>{row.ip}</TableCell>
                  <TableCell
                    className="max-w-[200px] cursor-pointer"
                    onClick={() => toggleExpand(i)}
                    title={expandedRows.has(i) ? "" : row.description}
                  >
                    {expandedRows.has(i) ? (
                      <span className="whitespace-pre-wrap break-all select-text">{row.description}</span>
                    ) : (
                      <span className="truncate block">
                        {row.description && row.description.length > 15
                          ? row.description.slice(0, 15) + "..."
                          : row.description}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={multiSelectMode ? 14 : 13}
                  className="h-20 text-center text-muted-foreground"
                >
                  {t("noLogs")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </div>

        {/* 分页控件 */}
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{t("pagination.pageSize") ?? "每页"}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>
              {t("pagination.total") ?? "共"} {totalCount} {t("pagination.items") ?? "条"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={14} />
            </Button>
            <span className="min-w-[4rem] text-center">
              {currentPage} / {Math.max(1, Math.ceil(totalCount / pageSize))}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </CardContent>

      {/* 批量导出邮箱确认弹窗 */}
      <Dialog open={batchExportOpen} onOpenChange={setBatchExportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("batchExportDialog.title")}</DialogTitle>
            <DialogDescription>{t("batchExportDialog.description")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            {/* 当前搜索条件摘要 */}
            {(() => {
              const activeFilters: { label: string; value: string }[] = [];
              if (filters.startDate) activeFilters.push({ label: t("filters.timeRange"), value: `${filters.startDate}${filters.endDate ? ` ~ ${filters.endDate}` : ""}` });
              if (filters.tokenName) activeFilters.push({ label: t("filters.tokenName"), value: filters.tokenName });
              if (filters.tokenGroup) activeFilters.push({ label: t("filters.tokenGroup"), value: filters.tokenGroup });
              if (filters.logType) activeFilters.push({ label: t("filters.logType"), value: renderType(mapType(Number(filters.logType))) });
              if (filters.model) activeFilters.push({ label: t("filters.model"), value: filters.model });
              if (filters.minUseTime) activeFilters.push({ label: t("filters.minUseTime"), value: `${filters.minUseTime}s` });
              if (filters.stream) activeFilters.push({ label: t("filters.stream"), value: filters.stream === "1" ? t("yes") : t("no") });
              if (filters.ip) activeFilters.push({ label: t("filters.ip"), value: filters.ip });
              return activeFilters.length > 0 ? (
                <div className="rounded-md border p-2 text-xs text-muted-foreground space-y-1">
                  <span className="font-medium text-foreground">{t("batchExportDialog.currentFilters")}</span>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    {activeFilters.map((f) => (
                      <span key={f.label}>{f.label}: <span className="text-foreground">{f.value}</span></span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-md border p-2 text-xs text-muted-foreground">
                  {t("batchExportDialog.noFilters")}
                </div>
              );
            })()}
            <Label htmlFor="export-count">{t("batchExportDialog.countLabel")}</Label>
            <Input
              id="export-count"
              type="number"
              value={exportCount}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v > 100000) {
                  setExportCount("100000");
                } else {
                  setExportCount(e.target.value);
                }
              }}
              min={1}
              max={100000}
              placeholder={t("batchExportDialog.countPlaceholder") ?? "最多 100000 条"}
            />
            <Label htmlFor="export-email">{t("batchExportDialog.emailLabel")}</Label>
            <Input
              id="export-email"
              type="email"
              value={exportEmail}
              onChange={(e) => setExportEmail(e.target.value)}
              placeholder={t("batchExportDialog.emailPlaceholder")}
              onKeyDown={(e) => { if (e.key === "Enter") handleBatchExportConfirm(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchExportOpen(false)}>
              {t("batchExportDialog.cancel")}
            </Button>
            <Button onClick={handleBatchExportConfirm} disabled={batchExporting}>
              {batchExporting ? t("messages.exporting") : t("batchExportDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
